import { checkExists } from 'external/dev_april_corgi+/js/common/asserts';
import { Disposable } from 'external/dev_april_corgi+/js/common/disposable';
import { Timer } from 'external/dev_april_corgi+/js/common/timer';

import {
  AIRPORTS_COLLECTION_ID,
  AIRSPACE_COLLECTION_ID,
  AIR_QUALITY_COLLECTION_ID,
  BURNT_AREAS_COLLECTION_ID,
  EUROPEAN_AIR_QUALITY_COLLECTION_ID,
  FIRE_PERIMETERS_COLLECTION_ID,
  JAPANESE_NATIONAL_PARKS_COLLECTION_ID,
  MOTOR_VEHICLE_USE_COLLECTION_ID,
  NAVAIDS_COLLECTION_ID,
  PROTECTED_AREAS_COLLECTION_ID,
  PUBLIC_LAND_COLLECTION_ID,
  REPORTING_POINTS_COLLECTION_ID,
  RIVER_GAUGES_COLLECTION_ID,
  TRAILCATALOG_PATHS_COLLECTION_ID,
  TRAILCATALOG_POINTS_COLLECTION_ID,
  WEATHER_STATIONS_COLLECTION_ID,
} from 'java/lat/trails/wire/wire';
import { aDescendsB, PointCategory } from 'java/org/trailcatalog/models/categories';
import { SimpleS2 } from 'java/org/trailcatalog/s2';
import { Camera } from 'js/map/camera';
import { COPERNICUS_DEM_COPYRIGHT, MAPTERHORN_COPYRIGHT, NASADEM_COPYRIGHT, OSM_COPYRIGHT, PROTOMAPS_COPYRIGHT, WORLDCOVER_COPYRIGHT } from 'js/map/common/copyrights';
import { Copyright, RgbaU32 } from 'js/map/common/types';
import { CompositeLayer } from 'js/map/layers/composite_layer';
import { LabelObstacles } from 'js/map/layers/label_placer';
import { CONTOURS_FEET, CONTOURS_METERS, MACROSTRAT, MbtileLayer } from 'js/map/layers/mbtile_layer';
import { RasterTileLayer } from 'js/map/layers/raster_tile_layer';
import { SlopeLayer } from 'js/map/layers/slope_layer';
import { StepLayer } from 'js/map/layers/step_layer';
import { StacLayer } from 'js/map/layers/stac_layer';
import { SwitchLayer } from 'js/map/layers/switch_layer';
import { WindVectorLayer } from 'js/map/layers/wind_vector_layer';
import { Renderer } from 'js/map/rendering/renderer';
import { Elevations } from 'js/map/workers/elevation/elevations';
import { Bands, Channel, Search } from 'js/map/workers/raster/stac_loader';
import { BASE_ZOOM as TERRARIUM_BASE_ZOOM, TILE_URL as TERRARIUM_TILE_URL } from 'js/map/workers/raster/terrarium';
import { EcmwfFormat, EcmwfRunFormat, PEAK_WINDOWS, SnodasFormat, WEATHER_VIEWS, WeatherView, WIND_LEVELS } from 'js/map/workers/raster/weather';
import { WindStyle } from 'js/map/workers/raster/wind_loader';
import { LocationIndex } from 'js/map/workers/routing/location_index';
import { Stream } from 'js/map/workers/s2/s2_streams';
import { Style } from 'js/map/workers/tiles/mbtile_loader';
import { Z_BASE_ELEVATION, Z_BASE_LAND_COVER, Z_BASE_SATELLITE, Z_BASE_SLOPE, Z_BASE_TERRAIN, Z_OVERLAY_TERRAIN } from 'js/map/z';
import { getUnitSystem, UnitSystem } from 'js/units/formatters';

import { OSM_NODES_SCHEMA, OSM_WAYS_SCHEMA } from '../model/schema';
import { readElevation, readGeology, readLandCover, readSnodas, readWeather } from '../sky/readings';
import {
  ControlValues,
  LayerControl,
  LayerEntry,
  layerEntries,
  LayerNode,
} from '../ui/layer_tree';
import { CollectionLayer } from './collection_layer';
import { PointCollectionLayer } from './point_collection_layer';
import { SkyLayer, ViewshedMode } from './sky_layer';
import {
  BASEMAP,
  BASEMAP_LABEL_KINDS,
  getDrawnCategories,
  getUserVisibleBasemap,
  getUserVisibleOsmWays,
  NODE_KINDS,
  OSM_WAYS,
  WAY_KINDS,
} from './styles';

const CC_BY_4 = {name: 'CC BY 4.0', url: 'https://creativecommons.org/licenses/by/4.0/'};
const POINT_LEVELS =
    {fromLevel: SimpleS2.HIGHEST_INDEX_LEVEL, toLevel: SimpleS2.HIGHEST_INDEX_LEVEL};
const WHOLE_COLLECTION_STREAMS: Stream[] =
    [{minZoom: 0, maxZoom: undefined, indexBottom: 4, fromLevel: 0, toLevel: undefined}];
// ECMWF's terms dictate the wording, the note that we modified the data, and the disclaimer.
// https://apps.ecmwf.int/datasets/licences/general/
const ECMWF_COPYRIGHT: Copyright = {
  long:
      'Based on data and products of the European Centre for Medium-Range Weather Forecasts '
          + '(ECMWF), retiled and modified by trails.lat. ECMWF does not accept any liability '
          + 'whatsoever for any error or omission in the data.',
  short: 'ECMWF',
  url: 'https://www.ecmwf.int/en/forecasts/datasets/open-data',
  license: CC_BY_4,
};
// Keep in sync with ecmwf.sh, which writes ecmwf_<run>_<layer>_<step>.pmtiles
const ECMWF_URL = 'https://tiles.trailcatalog.org/ecmwf_';
// ECMWF starts a run every 6 hours.
const ECMWF_CYCLE_HOURS = 6;
const HOUR_MS = 60 * 60 * 1000;
const PREFETCH_STEPS = 2;
// ECMWF tiles a run every 6 hours and its step closest to now moves every 3, so we check hourly.
const CONDITIONS_REFRESH_MS = HOUR_MS;
// NSIDC makes citing the data set a condition of using it.
const SNODAS_COPYRIGHT: Copyright = {
  long:
      'National Operational Hydrologic Remote Sensing Center. 2004. Snow Data Assimilation System '
          + '(SNODAS) Data Products at NSIDC, Version 1. Boulder, Colorado USA. NSIDC.',
  short: 'NOAA NOHRSC',
  url: 'https://doi.org/10.7265/N5TB14TC',
  publicDomain: true,
};
// openAIP's license allows shipping its data in an app as long as the data itself isn't what's
// sold.
// https://www.openaip.net/
const OPENAIP_COPYRIGHT: Copyright = {
  long: 'Aeronautical data from openAIP, not for navigation',
  short: 'openAIP',
  url: 'https://www.openaip.net/',
  license: {name: 'CC BY-NC 4.0', url: 'https://creativecommons.org/licenses/by-nc/4.0/'},
};
// The FAA asks for acknowledgement in products that use its charts.
const FAA_COPYRIGHT: Copyright = {
  long: 'Aeronautical charts from FAA Aeronautical Information Services',
  short: 'FAA',
  url: 'https://www.faa.gov/air_traffic/flight_info/aeronav/digital_products/',
  publicDomain: true,
};
// Keep in sync with faa_charts.py, which writes one archive per product and cycle named
// faa_<product>_<cycle>.pmtiles
const FAA_TILES_URL = 'https://tiles.trailcatalog.org/faa_';
// Charts take effect at 0901 UTC on the first day of their cycle. See faa_charts.py#CYCLE_EPOCH.
const FAA_CYCLE_EPOCH_MS = Date.UTC(2026, 8, 3, 9, 1);
const FAA_CYCLE_DAYS = 56;
type FaaProduct = 'sectional'|'terminal'|'enroute_low'|'enroute_high';
const FAA_PRODUCTS: FaaProduct[] = ['sectional', 'terminal', 'enroute_low', 'enroute_high'];
const SWISSTOPO_COPYRIGHT: Copyright = {
  long: 'Federal Office of Topography swisstopo',
  short: 'swisstopo',
  url: 'https://www.swisstopo.admin.ch/en/terms-of-use-free-geodata-and-geoservices',
};
// Sentinel-1A's first imagery is from October 2014
const SENTINEL_1_FIRST_YEAR = 2014;
// Sentinel-2A launched in June 2015
const SENTINEL_2_FIRST_YEAR = 2015;
// Cloud shadows, medium and high probability clouds, and cirrus in the scene classification
// https://docs.sentinel-hub.com/api/latest/data/sentinel-2-l2a/
const SENTINEL_2_MASK = {asset: 'scl', hidden: [3, 8, 9, 10]};
const SENTINEL_2_TRUE_COLOR: [Channel, Channel, Channel] =
    [byteSample('visual', 0), byteSample('visual', 1), byteSample('visual', 2)];
// Keyed by the value of the Sentinel-2 entry's bands control. sentinel2Bands adds the mask.
const SENTINEL_2_BANDS: {[key: string]: Bands} = {
  true_color: {channels: SENTINEL_2_TRUE_COLOR},
  // https://custom-scripts.sentinel-hub.com/custom-scripts/sentinel-2/swir-rgb/
  swir: {channels: [reflectance('swir22'), reflectance('nir08'), reflectance('red')]},
  // https://custom-scripts.sentinel-hub.com/custom-scripts/sentinel-2/false_color_infrared/
  infrared: {channels: [reflectance('nir'), reflectance('red'), reflectance('green')]},
  // https://custom-scripts.sentinel-hub.com/custom-scripts/sentinel-2/ndvi/
  ndvi: {
    channels: [normalizedDifference('nir', 'red')],
    palette: [
      [-0.5, 0x0c0c0c],
      [-0.2, 0xbfbfbf],
      [-0.1, 0xdbdbdb],
      [0, 0xeaeaea],
      [0.025, 0xfff9cc],
      [0.05, 0xede8b5],
      [0.075, 0xddd89b],
      [0.1, 0xccc682],
      [0.125, 0xbcb76b],
      [0.15, 0xafc160],
      [0.175, 0xa3cc59],
      [0.2, 0x91bf51],
      [0.25, 0x7fb247],
      [0.3, 0x70a33f],
      [0.35, 0x609635],
      [0.4, 0x4f892d],
      [0.45, 0x3f7c23],
      [0.5, 0x306d1c],
      [0.55, 0x216011],
      [0.6, 0x0f540a],
      [1, 0x004400],
    ].map(([at, color]) => ({at: indexByte(at), color})),
  },
  // https://custom-scripts.sentinel-hub.com/custom-scripts/sentinel-2/ndsi/
  ndsi: {
    channels: SENTINEL_2_TRUE_COLOR,
    highlight: {
      channel: normalizedDifference('green', 'swir16'),
      min: indexByte(0.42),
      color: 0x00ccff,
    },
  },
  // https://custom-scripts.sentinel-hub.com/custom-scripts/sentinel-2/scene-classification/
  scl: {
    channels: [{...byteSample('scl', 0), nearest: true}],
    palette: [
      0x000000, // no data
      0xff0000, // saturated or defective
      0x2f2f2f, // dark area or topographic shadow
      0x643200, // cloud shadow
      0x00a000, // vegetation
      0xffe65a, // not vegetated
      0x0000ff, // water
      0x808080, // unclassified
      0xc0c0c0, // cloud, medium probability
      0xffffff, // cloud, high probability
      0x64c8ff, // thin cirrus
      0xff96ff, // snow or ice
    ].map((color, at) => ({at, color})),
  },
};
// Backscatter mostly runs from -25 to 0 dB in VV and -30 to -5 dB in VH, and their ratio from 0 to
// 15 dB.
const SENTINEL_1_BANDS: Bands = {
  channels: [
    decibels('vv', undefined, -25, 0),
    decibels('vh', undefined, -30, -5),
    decibels('vv', 'vh', 0, 15),
  ],
};
const WORLDCOVER_CATALOG = {
  searchUrl: 'https://planetarycomputer.microsoft.com/api/stac/v1/search',
  collection: 'esa-worldcover',
  tokenUrl: 'https://planetarycomputer.microsoft.com/api/sas/v1/token/esa-worldcover',
};
// The 2021 map, which ESA made with a better algorithm than 2020's
const WORLDCOVER_SEARCH: Search = {
  from: '2021-01-01T00:00:00Z',
  to: '2021-12-31T23:59:59Z',
  query: {},
};
// Cropland, built up areas, water, wetland, and mangroves are left to the basemap, which draws
// them already.
// https://esa-worldcover.org/en/data-access
const LAND_COVER_BANDS: Bands = {
  channels: [{...byteSample('map', 0), nearest: true}],
  classes: [
    {value: 10, color: 0x2e7d32}, // tree cover
    {value: 20, color: 0xb8860b}, // shrubland
    {value: 30, color: 0xf4d35e}, // grassland
    {value: 60, color: 0xf4d35e}, // bare or sparse vegetation
    {value: 70, color: 0x7fc8f8}, // snow and ice
    {value: 100, color: 0xf4d35e}, // moss and lichen
  ],
  values: true,
};
// Keep in sync with snodas.sh, which writes one archive per day named snodas_<day>.pmtiles
const SNODAS_URL = 'https://tiles.trailcatalog.org/snodas_';
// NSIDC posts every day, so a newest day older than yesterday means snodas.sh stopped tiling. We
// reach back far enough to ride out a couple of days of that.
const SNODAS_LOOKBACK_DAYS = 3;
// Pressure levels in hPa by their altitude in the standard atmosphere
const FLIGHT_LEVELS: Record<number, string> = {
  925: 'FL025',
  850: 'FL050',
  700: 'FL100',
  600: 'FL140',
  500: 'FL180',
  400: 'FL240',
  300: 'FL300',
  250: 'FL340',
  200: 'FL390',
  150: 'FL450',
};
const CLOUD_HEIGHTS: Array<{view: WeatherView; label: string; name: string}> = [
  {view: 'clouds', label: 'All', name: 'Clouds'},
  {view: 'low_clouds', label: 'Low', name: 'Low clouds'},
  {view: 'medium_clouds', label: 'Medium', name: 'Medium clouds'},
  {view: 'high_clouds', label: 'High', name: 'High clouds'},
];
const LOOKAHEAD_WINDOWS =
    PEAK_WINDOWS.map(hours => [hours, hours < 48 ? `${hours} hours` : `${hours / 24} days`] as const);
// Runs land 6 to 9 hours after they start, and past that a stale run means ecmwf.sh stopped
// tiling. A 3 day old run still forecasts now, so we reach back that far.
const ECMWF_LOOKBACK_CYCLES = 12;
export const SKY_LAYER_ID = 'weather/sun_moon';
const SNOW_DEPTH_ID = 'us/conditions/snow_depth';
const SNOW_WATER_ID = 'us/conditions/snow_water';
// Where trailcatalog starts drawing its points, see trail_layer.ts#RENDER_POINT_ZOOM_THRESHOLD
const POINT_ICON_ZOOM = 14;
// Where the rest of the points join the landmarks, which would otherwise bury them
const POINT_ZOOM = 15;
// Where every landmark loads, rather than only the ones ranked to show by the zoom
const LANDMARK_ZOOM = 13;
const LANDMARKS = [
  PointCategory.NATURAL_BAY,
  PointCategory.NATURAL_GLACIER,
  PointCategory.NATURAL_LAKE,
  PointCategory.NATURAL_PEAK,
  PointCategory.NATURAL_STRAIT,
  PointCategory.NATURAL_VOLCANO,
  PointCategory.PARK,
  PointCategory.PLACE,
];
// Every category outside the landmarks, as the fewest ancestors, so its stream and the landmarks'
// share no point
const NOT_LANDMARKS = (() => {
  const others = (Object.values(PointCategory) as Array<string|number>)
      .filter((v): v is number => typeof v === 'number')
      .filter(v => !LANDMARKS.some(s => aDescendsB(v, s) || aDescendsB(s, v)));
  return others.filter(v => !others.some(o => o !== v && aDescendsB(v, o)));
})();

// The mbtiles rows that pmtiles convert copies into the metadata of every archive we tile
interface ArchiveMetadata {
  format: string;
  maxzoom: string;
}

// How an archive's tiles are stored
interface TileArchive {
  // pmtiles serve rejects a tile URL whose extension isn't this
  format: string;
  maxZoom: number;
}

const NO_TILE_ARCHIVE: TileArchive = {format: '', maxZoom: 0};

/** The map layers we offer besides a collection's own features. */
export class LayerCatalog extends Disposable {

  readonly contours: Record<UnitSystem, MbtileLayer>;
  // What the layer tree starts as
  readonly nodes: LayerNode[];
  // The start of the newest ECMWF run we know is tiled, in ms
  private ecmwfRun: number;
  // How that run is tiled and how each of its archives packs its pixels
  private ecmwfRunFormat: EcmwfRunFormat|undefined;
  // How that run's archives store their tiles
  private ecmwfArchive: TileArchive;
  // The time the weather layers forecast for in ms, or undefined to follow the clock
  private time: number|undefined;
  private readonly sky: SkyLayer;
  // The newest SNODAS day we know is tiled, or '' until we find one
  private snodasDay: string;
  // How that day's archive packs its pixels
  private snodasFormat: SnodasFormat;
  private snodasArchive: TileArchive;
  // The newest FAA cycle in effect that we know is tiled for each product, or '' until we find one
  private readonly faaCycles: Record<FaaProduct, string>;
  private readonly faaArchives: Record<FaaProduct, TileArchive>;
  private readonly conditionsRefresh: Timer;
  // What the OSM points drew, which the basemaps' labels make way for
  private readonly pointObstacles: LabelObstacles;

  constructor(
      locations: LocationIndex,
      // What the open collection drew, which the basemaps' labels make way for
      private readonly labelObstacles: LabelObstacles,
      private readonly camera: Camera,
      elevationSampler: Elevations,
      private readonly renderer: Renderer,
      // The tree as the viewer holds it, whose values a newer run or day reconfigures with
      private readonly current: () => LayerNode[],
      private readonly runChanged: () => void) {
    super();
    // We start on the current run and day, and showLatestWeather and showLatestSnow fall back to
    // older ones when those aren't tiled yet.
    const cycleMs = ECMWF_CYCLE_HOURS * HOUR_MS;
    this.ecmwfRun = Math.floor(Date.now() / cycleMs) * cycleMs;
    this.ecmwfRunFormat = undefined;
    this.ecmwfArchive = NO_TILE_ARCHIVE;
    this.time = undefined;
    this.sky = new SkyLayer(camera, elevationSampler, renderer);
    this.snodasDay = '';
    this.snodasFormat = {channels: []};
    this.snodasArchive = NO_TILE_ARCHIVE;
    this.faaCycles = {sectional: '', terminal: '', enroute_low: '', enroute_high: ''};
    this.faaArchives = {
      sectional: NO_TILE_ARCHIVE,
      terminal: NO_TILE_ARCHIVE,
      enroute_low: NO_TILE_ARCHIVE,
      enroute_high: NO_TILE_ARCHIVE,
    };
    this.conditionsRefresh = new Timer(CONDITIONS_REFRESH_MS, () => {
      this.showLatestFaaCharts();
      this.showLatestSnow();
      this.showLatestWeather();
    });
    this.registerDisposable(this.conditionsRefresh);

    this.pointObstacles = new LabelObstacles();
    this.contours = {
      imperial: this.newContourLayer('imperial'),
      metric: this.newContourLayer('metric'),
    };

    const ways =
        new CollectionLayer(
            [OSM_COPYRIGHT],
            `/api/collections/${TRAILCATALOG_PATHS_COLLECTION_ID}`,
            OSM_WAYS,
            [
              // Snap to about a pixel at each zoom, which is level z + 6. A level L cell's half
              // diagonal is 0.388 * 2^-L in Mercator units and a pixel at zoom z is 2^-(z+7).
              //
              // This is where most of the geometry goes: consecutive OSM nodes on a path sit about
              // 15 m apart, against 38 m to the pixel at zoom 12.
              //
              // One band per zoom is cheap because zooming out reuses what is already held. Only
              // zooming in asks for a finer snap, and a finer snap is a different URL.
              {minZoom: 0, snap: 16},
              {minZoom: 11, snap: 17},
              {minZoom: 12, snap: 18},
              {minZoom: 13, snap: 19},
              {minZoom: 14, snap: 20},
              {minZoom: 15, snap: undefined},
            ],
            [
              // Each band fetches the S2 cells of one level, and the level goes up by one every two
              // zooms. Finer cells mean more requests, and coarser cells reach further past the
              // screen. This schedule keeps a viewport near 50 requests that cover about 5x the
              // screen. One level for every zoom does not work: level 11 takes 903 requests at
              // zoom 10 and covers 30x the screen at zoom 15.
              //
              // A cell's ways arrive in one response, which the loader decodes in one task, so a
              // coarse cell in a dense city is one very large download and one long decode. The
              // worst level 9 cell is central Berlin, with 221k paths in 3.7 MB, and a level 8
              // cell covers four times the area. So the schedule starts at level 9 for zoom 10.
              //
              // Bands also start at zooms 11 and 13, because the style draws more from there:
              // paths and residential streets from 11, service roads from 13. Each band asks the
              // server only for the categories it draws, which removes 82% of the ways in a
              // Seattle viewport at zoom 10 and 37% at zoom 12.
              //
              // Splitting into fine-grained bands is cheap. Zooming in already refetches every
              // cell at each integer zoom up to 15, because the snap level changes. Only zooming
              // out across a band boundary refetches cells that could have been reused.
              //
              // These streams carry the short ways, whose smallest containing cell is level 11 or
              // finer. The streams below carry the longer ways, so every way is covered.
              {minZoom: 10, maxZoom: 11, indexBottom: 9, fromLevel: 11, toLevel: undefined},
              {minZoom: 11, maxZoom: 12, indexBottom: 9, fromLevel: 11, toLevel: undefined},
              {minZoom: 12, maxZoom: 13, indexBottom: 10, fromLevel: 11, toLevel: undefined},
              {minZoom: 13, maxZoom: 14, indexBottom: 10, fromLevel: 11, toLevel: undefined},
              {minZoom: 14, maxZoom: undefined, indexBottom: 11, fromLevel: 11, toLevel: undefined},
              // Carry the long ways separately, or a river keeps its short ways and loses the long
              // stretches of its mainstem. A way is assigned to the smallest cell containing it and
              // a range query only reaches cells at or below its own level, so nothing above can
              // pick these up. 153k paths sit above level 10, 42% of them waterways, and the share
              // climbs with length: 87 of the 101 paths at level 4.
              //
              // Tile the two ends differently because they cost differently. Levels 0 to 8 are
              // rare enough for level 4, where the worst tile on the planet holds 188 and a real
              // one runs about 25 kb. Levels 9 and 10 are 25x more numerous, so they need level 7
              // for 9 to 42 kb against the 1.5 MB a level 4 tile of them costs.
              //
              // fromLevel 0 buys the 18 ways above level 4 for 8 to 19 ancestor requests a
              // viewport. They are ferry routes and a rail line, and a layer that draws every way
              // draws those.
              {minZoom: 10, maxZoom: undefined, indexBottom: 4, fromLevel: 0, toLevel: 8},
              {minZoom: 10, maxZoom: undefined, indexBottom: 7, fromLevel: 9, toLevel: 10},
            ].map(stream => ({
              ...stream,
              // From the full style rather than the kinds the Ways layer shows, so that toggling a
              // kind restyles what is held instead of refetching
              categories: getDrawnCategories(OSM_WAYS, stream.minZoom, stream.maxZoom),
            })),
            locations,
            this.camera,
            this.renderer,
        );
    const osmNodes =
        new PointCollectionLayer(
            [OSM_COPYRIGHT],
            `/api/collections/${TRAILCATALOG_POINTS_COLLECTION_ID}`,
            [
              // Below LANDMARK_ZOOM each zoom asks only for the landmarks ranked to show by then,
              // so a continent is its countries and states rather than every lake in it. Every
              // point sits in a SimpleS2.HIGHEST_INDEX_LEVEL cell. Level 11 tiles hold a viewport
              // at zoom 14 in a handful of requests, and level 10 one at zoom 13, so each zoom
              // tiles three levels above itself.
              ...Array.from({length: LANDMARK_ZOOM}, (_, zoom) => ({
                minZoom: zoom,
                maxZoom: zoom + 1,
                indexBottom: Math.max(0, zoom - 3),
                ...POINT_LEVELS,
                categories: LANDMARKS,
                maxRank: zoom,
              })),
              {minZoom: LANDMARK_ZOOM, maxZoom: undefined, indexBottom: 10, ...POINT_LEVELS,
                categories: LANDMARKS},
              {minZoom: POINT_ZOOM, maxZoom: undefined, indexBottom: 11, ...POINT_LEVELS,
                categories: NOT_LANDMARKS},
            ],
            NODE_KINDS.filter(({hidden}) => !hidden).map(({category}) => category),
            POINT_ICON_ZOOM,
            this.camera,
            this.renderer,
            this.labelObstacles,
            this.pointObstacles,
        );
    const protomaps =
        new MbtileLayer(
            [PROTOMAPS_COPYRIGHT],
            'https://api.protomaps.com/tiles/v4/${id.zoom}/${id.x}/${id.y}.mvt?key=b6ce0acec3807d5c',
            BASEMAP,
            /* extraZoom= */ 0,
            /* minZoom= */ 0,
            /* maxZoom= */ 15,
            this.renderer,
            [this.labelObstacles, this.pointObstacles],
        );
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
    const sentinelValues = {
      bands: 'true_color',
      fill_clouds: true,
      from: isoDay(fiveDaysAgo),
      to: isoDay(new Date()),
      cloud: 50,
    };
    const sentinel =
        new StacLayer(
            {
              searchUrl: 'https://earth-search.aws.element84.com/v1/search',
              collection: 'sentinel-2-c1-l2a',
            },
            sentinelSearch(sentinelValues),
            sentinel2Bands(sentinelValues),
            SENTINEL_2_FIRST_YEAR,
            copernicusCredit,
            Z_BASE_SATELLITE,
            this.renderer);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const sentinel1Values = {from: isoDay(weekAgo), to: isoDay(new Date())};
    const sentinel1 =
        new StacLayer(
            {
              searchUrl: 'https://planetarycomputer.microsoft.com/api/stac/v1/search',
              collection: 'sentinel-1-rtc',
              tokenUrl: 'https://planetarycomputer.microsoft.com/api/sas/v1/token/sentinel-1-rtc',
            },
            sentinelSearch(sentinel1Values),
            SENTINEL_1_BANDS,
            SENTINEL_1_FIRST_YEAR,
            sentinel1Credit,
            Z_BASE_SATELLITE,
            this.renderer);
    const landCover =
        new StacLayer(
            WORLDCOVER_CATALOG,
            WORLDCOVER_SEARCH,
            LAND_COVER_BANDS,
            /* firstYear= */ 2021,
            () => [WORLDCOVER_COPYRIGHT],
            Z_BASE_LAND_COVER,
            this.renderer,
            /* tint= */ 0xFFFFFF73 as RgbaU32);
    const geology =
        new MbtileLayer(
            [{
              long: 'Geologic maps compiled by Macrostrat from their original sources',
              short: 'Macrostrat',
              url: 'https://macrostrat.org/',
              license: CC_BY_4,
            }],
            'https://tiles.macrostrat.org/carto/${id.zoom}/${id.x}/${id.y}.mvt',
            MACROSTRAT,
            /* extraZoom= */ 0,
            /* minZoom= */ 0,
            // Past 12 the tiles only overzoom the same units.
            /* maxZoom= */ 12,
            this.renderer);
    const elevations =
        new RasterTileLayer(
            [MAPTERHORN_COPYRIGHT],
            TERRARIUM_TILE_URL,
            /* tint= */ 0xFFFFFFFF as RgbaU32,
            /* z= */ Z_BASE_ELEVATION,
            /* extraZoom= */ 0,
            /* minZoom= */ 0,
            /* maxZoom= */ TERRARIUM_BASE_ZOOM,
            this.renderer,
            'elevation');

    this.nodes = [{
      kind: 'group',
      id: 'aviation',
      name: 'Aviation',
      enabled: true,
      children: [
        this.newCollectionEntry(
            'aviation/airports',
            'Airports',
            AIRPORTS_COLLECTION_ID,
            OPENAIP_COPYRIGHT,
            // About 47,000, almost half of them in the US
            getPointStreams(/* zoom= */ 7, /* indexBottom= */ 6),
            locations),
        this.newCollectionEntry(
            'aviation/airspace',
            'Airspace',
            AIRSPACE_COLLECTION_ID,
            OPENAIP_COPYRIGHT,
            // About 34,000 worldwide, packed densest over Europe
            getPolygonStreams(),
            locations),
        this.newCollectionEntry(
            'aviation/navaids',
            'Navaids',
            NAVAIDS_COLLECTION_ID,
            OPENAIP_COPYRIGHT,
            // About 4,900, which is as many as the European air quality stations
            getPointStreams(/* zoom= */ 4, /* indexBottom= */ 4),
            locations),
        this.newCollectionEntry(
            'aviation/reporting_points',
            'Reporting points',
            REPORTING_POINTS_COLLECTION_ID,
            OPENAIP_COPYRIGHT,
            // About 6,000, almost all in Europe and clustered around airports
            getPointStreams(/* zoom= */ 6, /* indexBottom= */ 6),
            locations),
        {
          ...this.newCollectionEntry(
            'aviation/weather_stations',
            'Weather stations',
            WEATHER_STATIONS_COLLECTION_ID,
            {
              long: 'Weather stations from the NOAA Aviation Weather Center',
              short: 'NOAA AWC',
              url: 'https://aviationweather.gov/',
              publicDomain: true,
            },
            // About 7,700 that report METARs, tiled like the river gauges
            getPointStreams(/* zoom= */ 6, /* indexBottom= */ 6),
            locations),
          weatherStation: {id: 'station', reports: 'reports'},
        },
        {
          kind: 'group',
          id: 'us/faa',
          name: 'FAA',
          enabled: true,
          children: [
            this.newFaaEntry('enroute_high', 'IFR Enroute High', /* z= */ Z_BASE_SATELLITE),
            this.newFaaEntry('enroute_low', 'IFR Enroute Low', /* z= */ Z_BASE_SATELLITE),
            this.newFaaEntry('sectional', 'Sectional', /* z= */ Z_BASE_SATELLITE),
            this.newFaaEntry(
                'terminal',
                'Terminal Area',
                // Drawn over the sectional it details
                /* z= */ Z_BASE_SATELLITE + 0.1),
          ],
        },
      ],
    }, {
      kind: 'group',
      id: 'osm',
      name: 'OpenStreetMap',
      enabled: true,
      children: [{
        kind: 'layer',
        id: 'osm/protomaps',
        name: 'Basemap',
        enabled: true,
        layer: protomaps,
        controls: [
          {kind: 'checkbox', key: 'boundaries', label: 'Boundaries'},
          ...BASEMAP_LABEL_KINDS.map(({key, label}) => ({kind: 'checkbox' as const, key, label})),
        ],
        values: {
          boundaries: true,
          ...Object.fromEntries(BASEMAP_LABEL_KINDS.map(({key}) => [key, true])),
        },
        configure: values => {
          protomaps.setStyle(
              getUserVisibleBasemap(
                  values.boundaries === true,
                  new Set(
                      BASEMAP_LABEL_KINDS
                          .filter(({key}) => values[key])
                          .flatMap(({layerNames}) => layerNames))));
        },
      }, {
        kind: 'layer',
        id: 'osm/nodes',
        name: 'Nodes',
        enabled: true,
        schema: OSM_NODES_SCHEMA,
        layer: osmNodes,
        controls: NODE_KINDS.map(({key, label}) => ({kind: 'checkbox', key, label})),
        values: Object.fromEntries(NODE_KINDS.map(({key, hidden}) => [key, !hidden])),
        configure: values => {
          osmNodes.setCategories(
              NODE_KINDS.filter(({key}) => values[key]).map(({category}) => category));
        },
      }, {
        kind: 'layer',
        id: 'osm/ways',
        name: 'Ways',
        // On by default because BASEMAP hands it the ways.
        enabled: true,
        schema: OSM_WAYS_SCHEMA,
        layer: ways,
        controls: WAY_KINDS.map(({key, label}) => ({kind: 'checkbox', key, label})),
        values: Object.fromEntries(WAY_KINDS.map(({key}) => [key, true])),
        configure: values => {
          ways.setStyle(
              getUserVisibleOsmWays(
                  WAY_KINDS.filter(({key}) => values[key]).map(({category}) => category)));
        },
      }],
    }, {
      kind: 'group',
      id: 'national',
      name: 'Regional',
      enabled: true,
      // Children keep their top level ids because saved view states refer to layers by id.
      children: [{
        kind: 'group',
        id: 'europe',
        name: 'Europe',
        enabled: true,
        children: [
          this.newCollectionEntry(
              'europe/air_quality',
              'Air quality',
              EUROPEAN_AIR_QUALITY_COLLECTION_ID,
              {
                long: 'European Air Quality Index from the European Environment Agency, unverified',
                short: 'EEA',
                url: 'https://airindex.eea.europa.eu/AQI/index.html',
                license: CC_BY_4,
              },
              // About 3,500
              getPointStreams(/* zoom= */ 4, /* indexBottom= */ 4),
              locations),
          this.newCollectionEntry(
              'europe/burnt_areas',
              'Burnt areas',
              BURNT_AREAS_COLLECTION_ID,
              {
                long:
                    'Burnt areas from the last 30 days from the European Forest Fire Information '
                        + 'System. Contains modified Copernicus Emergency Management Service '
                        + `information ${new Date().getUTCFullYear()}`,
                short: 'EFFIS',
                url: 'https://forest-fire.emergency.copernicus.eu/',
                license: CC_BY_4,
              },
              // Few enough to load all of Europe at once: 1,318 areas were 7.4 MB of GeoJSON on
              // 2026-10-13.
              WHOLE_COLLECTION_STREAMS,
              locations),
          this.newCollectionEntry(
              'europe/protected_areas',
              'Protected areas',
              PROTECTED_AREAS_COLLECTION_ID,
              {
                long: 'Nationally designated areas 2026 from the European Environment Agency',
                short: 'EEA',
                url: 'https://doi.org/10.2909/028003e7-7585-4d69-92fc-7f81e0cc2340',
                license: CC_BY_4,
              },
              getPolygonStreams(),
              locations),
        ],
      }, {
        kind: 'group',
        id: 'fr',
        name: 'France',
        enabled: true,
        children: [
          this.newIgnEntry(
              'fr/orthophotos', 'Orthophotos', 'BD ORTHO orthophotos', 'ORTHOIMAGERY.ORTHOPHOTOS',
              'image/jpeg'),
          this.newIgnEntry(
              'fr/plan_ign', 'Plan IGN', 'Plan IGN v2', 'GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2',
              'image/png'),
        ],
      }, {
        kind: 'group',
        id: 'jp',
        name: 'Japan',
        enabled: true,
        children: [
          this.newCollectionEntry(
              'jp/national_parks',
              'National parks',
              JAPANESE_NATIONAL_PARKS_COLLECTION_ID,
              {
                // PDL 1.0 requires saying the data was processed and by whom.
                long:
                    'National park zones from the Biodiversity Center of Japan, Ministry of the '
                        + 'Environment, processed by trails.lat',
                short: 'MOE',
                url: 'https://www.biodic.go.jp/nps_nwp_nca_cz.html',
              },
              getPolygonStreams(),
              locations),
        ],
      }, {
        kind: 'group',
        id: 'ch',
        name: 'Switzerland',
        enabled: true,
        children: [
          this.newSwisstopoEntry(
              'ch/topo', 'Swiss Topo', 'ch.swisstopo.pixelkarte-farbe', /* extension= */ 'jpeg'),
        ],
      }, {
        kind: 'group',
        id: 'us',
        name: 'United States',
        enabled: true,
        children: [{
          kind: 'group',
          id: 'us/conditions',
          name: 'Conditions',
          enabled: true,
          children: [
            this.newCollectionEntry(
                'us/conditions/air_quality',
                'Air quality',
                AIR_QUALITY_COLLECTION_ID,
                {
                  // AirNow's guidelines ask for the agencies to be credited first.
                  // https://docs.airnowapi.org/docs/DataUseGuidelines.pdf
                  long:
                      'Current air quality index contours from state, local, and tribal air '
                          + 'quality agencies and the US EPA AirNow program, preliminary and '
                          + 'unverified',
                  short: 'AirNow',
                  url: 'https://www.airnow.gov/',
                  publicDomain: true,
                },
                // Few enough to load the whole country at once: 79 contours were 180 kB of GeoJSON
                // on 2026-10-03.
                WHOLE_COLLECTION_STREAMS,
                locations),
            this.newCollectionEntry(
                'us/conditions/fire_perimeters',
                'Fire perimeters',
                FIRE_PERIMETERS_COLLECTION_ID,
                {
                  long:
                      'Current interagency fire perimeters from the National Interagency Fire '
                          + 'Center',
                  short: 'NIFC',
                  url: 'https://data-nifc.opendata.arcgis.com/',
                  publicDomain: true,
                },
                // Few enough to load the whole country at once: 113 perimeters were 413 kB on
                // 2026-10-03.
                WHOLE_COLLECTION_STREAMS,
                locations),
            this.newArcGisExportEntry(
                'us/conditions/radar',
                'NOAA radar',
                {
                  long: 'Multi-Radar Multi-Sensor base reflectivity from NOAA',
                  short: 'NOAA MRMS',
                  url: 'https://radar.weather.gov/',
                  publicDomain: true,
                },
                'eventdriven/rest/services/radar/radar_base_reflectivity/MapServer',
                // MRMS has 1 km cells and a zoom 9 pixel is about 300 m, so more zoom adds no
                // detail.
                /* maxZoom= */ 9),
            this.newCollectionEntry(
                'us/conditions/river_gauges',
                'River gauges',
                RIVER_GAUGES_COLLECTION_ID,
                {
                  long: 'River observations from the NOAA National Water Prediction Service',
                  short: 'NOAA NWPS',
                  url: 'https://water.noaa.gov/',
                  publicDomain: true,
                },
                // About 11,500
                getPointStreams(/* zoom= */ 6, /* indexBottom= */ 6),
                locations),
            this.newSnowEntry(SNOW_DEPTH_ID, 'Snow depth', 'depth'),
            this.newSnowEntry(SNOW_WATER_ID, 'Snow water equivalent', 'water'),
          ],
        },
        this.newCollectionEntry(
            'us/public_land',
            'Public land',
            PUBLIC_LAND_COLLECTION_ID,
            {
              long:
                  'Protected Areas Database of the United States (PAD-US) 4.1 from the USGS Gap '
                      + 'Analysis Project',
              short: 'USGS PAD-US',
              url: 'https://doi.org/10.5066/P96WBCHS',
              publicDomain: true,
            },
            getPolygonStreams(),
            locations),
        {
          kind: 'layer',
          id: 'us/topo',
          name: 'US Topo',
          enabled: false,
          layer: new RasterTileLayer(
              [{
                long:
                    'Map services and data available from U.S. Geological Survey, National '
                        + 'Geospatial Program',
                short: 'USGS',
                url: 'https://www.usgs.gov/programs/national-geospatial-program/national-map',
                publicDomain: true,
              }],
              'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile'
                  + '/${id.zoom}/${id.y}/${id.x}',
              /* tint= */ 0xFFFFFFFF as RgbaU32,
              /* z= */ Z_BASE_SATELLITE,
              /* extraZoom= */ 0,
              /* minZoom= */ 0,
              // USGS caches tiles through zoom 15 and 404s past it.
              /* maxZoom= */ 15,
              this.renderer,
          ),
          controls: [],
          values: {},
        },
        this.newCollectionEntry(
            'us/usfs_mvum',
            'USFS MVUM',
            MOTOR_VEHICLE_USE_COLLECTION_ID,
            {
              long: 'Motor Vehicle Use Maps from the USDA Forest Service',
              short: 'USFS MVUM',
              url: 'https://data.fs.usda.gov/geodata/edw/datasets.php',
              publicDomain: true,
            },
            [{minZoom: 10, maxZoom: undefined, indexBottom: 8, fromLevel: 0, toLevel: undefined}],
            locations),
        ],
      }],
    }, {
      kind: 'group',
      id: 'satellite',
      name: 'Satellite',
      enabled: true,
      children: [{
        kind: 'group',
        id: 'satellite/goes',
        name: 'GOES',
        enabled: true,
        children: [
          this.newGoesEntry('East', 'FireTemp', 'fire temperature'),
          this.newGoesEntry('East', 'GeoColor', 'GeoColor'),
          this.newGoesEntry('West', 'FireTemp', 'fire temperature'),
          this.newGoesEntry('West', 'GeoColor', 'GeoColor'),
        ],
      }, {
        kind: 'layer',
        id: 'satellite/sentinel_1',
        name: 'Sentinel-1 RTC',
        enabled: false,
        layer: sentinel1,
        controls: [
          {kind: 'date', key: 'from', label: 'From'},
          {kind: 'date', key: 'to', label: 'To'},
        ],
        values: sentinel1Values,
        configure: values => {
          sentinel1.setSearch(sentinelSearch(values), SENTINEL_1_BANDS);
        },
      }, {
        kind: 'layer',
        id: 'satellite/sentinel',
        name: 'Sentinel-2 L2A',
        enabled: false,
        layer: sentinel,
        controls: [
          {
            kind: 'select',
            key: 'bands',
            label: 'Bands',
            options: [
              {value: 'infrared', label: 'Color infrared'},
              {value: 'scl', label: 'Scene classification'},
              {value: 'swir', label: 'Shortwave infrared'},
              {value: 'ndsi', label: 'Snow index (NDSI)'},
              {value: 'true_color', label: 'True color'},
              {value: 'ndvi', label: 'Vegetation index (NDVI)'},
            ],
          },
          {kind: 'checkbox', key: 'fill_clouds', label: 'Fill clouds', setting: true},
          {kind: 'date', key: 'from', label: 'From'},
          {kind: 'date', key: 'to', label: 'To'},
          {kind: 'percent', key: 'cloud', label: 'Max cloud cover'},
        ],
        values: sentinelValues,
        configure: values => {
          sentinel.setSearch(sentinelSearch(values), sentinel2Bands(values));
        },
      }],
    }, {
      kind: 'group',
      id: 'weather',
      name: 'Sky',
      enabled: true,
      children: [
        this.newSwitchedWeatherEntry(
            'weather/turbulence',
            'Clear air turbulence',
            [altitudeControl([[400, 300], [300, 250], [250, 200], [200, 150]].map(
                ([bottom, top]) => ({
                  value: `${bottom}_${top}`,
                  label: `${FLIGHT_LEVELS[bottom]} to ${FLIGHT_LEVELS[top]}`,
                })))],
            {altitude: '400_300'},
            values => {
              const [bottom, top] = String(values.altitude).split('_').map(Number);
              return {
                view: `turbulence_${values.altitude}` as WeatherView,
                name: `Turbulence, ${FLIGHT_LEVELS[bottom]} to ${FLIGHT_LEVELS[top]}`,
              };
            }),
        this.newSwitchedWeatherEntry(
            'weather/clouds',
            'Clouds',
            [{
              kind: 'select',
              key: 'height',
              label: 'Height',
              options: CLOUD_HEIGHTS.map(({view, label}) => ({value: view, label})),
            }],
            {height: 'clouds'},
            values => checkExists(CLOUD_HEIGHTS.find(({view}) => view === values.height))),
        this.newWeatherEntry('dew_point', 'Dew point'),
        this.newWeatherEntry('freezing', 'Freezing altitude'),
        this.newWeatherEntry('humidity', 'Humidity'),
        this.newSwitchedWeatherEntry(
            'weather/icing',
            'Icing',
            [altitudeControl([850, 700, 600, 500, 400, 300].map(level => ({
              value: String(level),
              label: FLIGHT_LEVELS[level],
            })))],
            {altitude: '850'},
            values => ({
              view: `icing_${values.altitude}` as WeatherView,
              name: `Icing at ${FLIGHT_LEVELS[Number(values.altitude)]}`,
            })),
        this.newWeatherEntry('pressure', 'Pressure'),
        this.newSwitchedWeatherEntry(
            'weather/rain',
            'Rain and thunder',
            [lookaheadControl('accumulation', 'Accumulation')],
            {accumulation: 'none'},
            values => values.accumulation === 'none'
                ? {view: 'precipitation', name: 'Rain and thunder'}
                : lookaheadView('rain', 'Rain', values.accumulation)),
        this.newSwitchedWeatherEntry(
            'weather/snow',
            'Snow',
            [lookaheadControl('accumulation', 'Accumulation')],
            {accumulation: 'none'},
            values => values.accumulation === 'none'
                ? {view: 'snow_depth', name: 'Snow depth'}
                : lookaheadView('snowfall', 'Snow', values.accumulation)),
        {
          kind: 'layer',
          id: SKY_LAYER_ID,
          name: 'Sun and moon',
          enabled: false,
          layer: this.sky,
          controls: [
            {kind: 'checkbox', key: 'sunlight', label: 'Sunlight shading'},
            {kind: 'checkbox', key: 'moonlight', label: 'Moonlight shading'},
            {
              kind: 'select',
              key: 'viewshed',
              label: 'Viewshed',
              options: [
                {value: 'none', label: 'None'},
                {value: 'pin', label: 'From pin'},
                {value: 'subject', label: 'Of subject'},
              ],
            },
          ],
          values: {viewshed: 'none', sunlight: false, moonlight: false},
          timed: 'any',
          persisted: ['pin', 'subject'],
          configure: values => {
            this.sky.setPins(
                typeof values.pin === 'string' ? values.pin : undefined,
                typeof values.subject === 'string' ? values.subject : undefined);
            this.sky.setViewshed(values.viewshed as ViewshedMode);
            this.sky.setSunlight(values.sunlight === true);
            this.sky.setMoonlight(values.moonlight === true);
          },
          onShown: () => {
            this.sky.appear();
          },
          // Only a click on a pin, so that the rows describe what the rays show
          readings: point => this.sky.getReadingsNear(point, this.time ?? Date.now()),
        },
        this.newWeatherEntry('temperature', 'Temperature'),
        this.newWeatherEntry('thermals', 'Thermals'),
        this.newWeatherEntry('waves', 'Waves'),
        this.newWeatherEntry('wet_bulb', 'Wet-bulb temperature'),
        this.newWindEntry(),
      ],
    }, {
      kind: 'group',
      id: 'topography',
      name: 'Terrain',
      enabled: true,
      children: [{
        kind: 'layer',
        id: 'topography/contours',
        name: 'Contours',
        enabled: true,
        layer: this.contours[getUnitSystem()],
        controls: [],
        values: {},
      }, {
        kind: 'layer',
        id: 'topography/elevations',
        name: 'Elevation colors',
        enabled: false,
        layer: elevations,
        controls: [],
        values: {},
        readout: {
          name: 'Elevation',
          read: point => {
            const packed = elevations.packedAt(point);
            return packed !== undefined ? readElevation(packed) : undefined;
          },
        },
      }, {
        kind: 'layer',
        id: 'topography/geology',
        name: 'Geology',
        enabled: false,
        layer: geology,
        controls: [],
        values: {},
        readings: point => {
          const unit = geology.featuresAt(point)[0];
          return unit ? readGeology(unit) : [];
        },
      }, {
        kind: 'layer',
        id: 'topography/hillshades',
        name: 'Hillshades',
        enabled: true,
        layer: new RasterTileLayer(
            [COPERNICUS_DEM_COPYRIGHT, NASADEM_COPYRIGHT],
            'https://tiles.trailcatalog.org/hillshades/${id.zoom}/${id.x}/${id.y}.webp',
            /* tint= */ 0xFFFFFF30 as RgbaU32,
            /* z= */ Z_BASE_TERRAIN,
            /* extraZoom= */ 0,
            /* minZoom= */ 0,
            /* maxZoom= */ 12,
            this.renderer,
            /* decode= */ 'hillshade',
        ),
        controls: [],
        values: {},
      }, {
        kind: 'layer',
        id: 'topography/land_cover',
        name: 'Land cover',
        enabled: false,
        layer: landCover,
        controls: [],
        values: {},
        readout: {
          name: 'Land cover',
          read: point => {
            const value = landCover.valueAt(point);
            return value !== undefined ? readLandCover(value) : undefined;
          },
        },
      }, {
        kind: 'layer',
        id: 'topography/slopes',
        name: 'Slope angles',
        enabled: false,
        layer: new SlopeLayer([MAPTERHORN_COPYRIGHT], Z_BASE_SLOPE, this.renderer),
        controls: [],
        values: {},
      }],
    }];
    const owned =
        new Set([
          ...layerEntries(this.nodes).map(({entry}) => entry.layer),
          ...Object.values(this.contours),
        ]);
    for (const layer of owned) {
      this.registerDisposable(layer);
    }
  }

  // Points the conditions layers at the newest tiles, and keeps checking for newer ones.
  start(): void {
    this.showLatestFaaCharts();
    this.showLatestSnow();
    this.showLatestWeather();
    this.conditionsRefresh.start();
  }

  private newContourLayer(system: UnitSystem): MbtileLayer {
    const archive = system === 'imperial' ? 'contours_ft' : 'contours_m';
    return new MbtileLayer(
        [COPERNICUS_DEM_COPYRIGHT, NASADEM_COPYRIGHT],
        'https://tiles.trailcatalog.org/${archive}/${id.zoom}/${id.x}/${id.y}.mvt'
            .replace('${archive}', archive),
        system === 'imperial' ? CONTOURS_FEET : CONTOURS_METERS,
        /* extraZoom= */ 0,
        /* minZoom= */ 9,
        /* maxZoom= */ 14,
        this.renderer,
        [this.labelObstacles, this.pointObstacles]);
  }

  private newGoesEntry(satellite: 'East'|'West', product: string, label: string): LayerEntry {
    return {
      kind: 'layer',
      id: `satellite/goes/${satellite}_${product}`,
      name: `${satellite} ${label}`,
      enabled: false,
      layer: new RasterTileLayer(
          [
            {
              long:
                  `GOES-${satellite} ABI imagery from NOAA, tiled by NASA Global Imagery Browse `
                      + 'Services',
              short: 'NOAA/NASA GIBS',
              url: 'https://nasa-gibs.github.io/gibs-api-docs/',
              publicDomain: true,
            },
          ],
          // The path is style/time/matrix set, and "default" for time means the most recent scan
          // GIBS has finished tiling, which runs about 20 minutes behind the satellite.
          `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GOES-${satellite}_ABI_${product}`
              + '/default/default/GoogleMapsCompatible_Level7'
              + '/${id.zoom}/${id.y}/${id.x}.png',
          /* tint= */ 0xFFFFFFFF as RgbaU32,
          /* z= */ Z_OVERLAY_TERRAIN,
          /* extraZoom= */ 0,
          /* minZoom= */ 0,
          // GoogleMapsCompatible_Level7 stops at zoom 7, which is about the 2 km resolution ABI
          // gives away from the sub-satellite point anyway.
          /* maxZoom= */ 7,
          this.renderer,
      ),
      controls: [],
      values: {},
    };
  }

  // https://www.geo.admin.ch/en/wmts-available-services-and-data
  private newSwisstopoEntry(
      id: string,
      name: string,
      layer: string,
      extension: string): LayerEntry {
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer: new RasterTileLayer(
          [SWISSTOPO_COPYRIGHT],
          `https://wmts.geo.admin.ch/1.0.0/${layer}/default/current/3857`
              + `/\${id.zoom}/\${id.x}/\${id.y}.${extension}`,
          /* tint= */ 0xFFFFFFFF as RgbaU32,
          Z_BASE_SATELLITE,
          /* extraZoom= */ 0,
          /* minZoom= */ 0,
          /* maxZoom= */ 19,
          this.renderer,
      ),
      controls: [],
      values: {},
    };
  }

  // The public Géoplateforme WMTS is the only IGN service without a per-IP quota, and it only
  // takes KVP requests.
  // https://cartes.gouv.fr/aide/fr/guides-utilisateur/utiliser-les-services-de-la-geoplateforme/limites-d-usage/
  private newIgnEntry(
      id: string,
      name: string,
      product: string,
      layer: string,
      format: string): LayerEntry {
    const params = new URLSearchParams({
      SERVICE: 'WMTS',
      REQUEST: 'GetTile',
      VERSION: '1.0.0',
      LAYER: layer,
      STYLE: 'normal',
      FORMAT: format,
      TILEMATRIXSET: 'PM',
    });
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer: new RasterTileLayer(
          // The Licence Ouverte asks for the date of the last update, and the Géoplateforme
          // serves the current edition.
          [{
            long:
                `${product} from the Institut national de l'information géographique et `
                    + `forestière, current edition as of ${new Date().getUTCFullYear()}`,
            short: 'IGN',
            url: 'https://cartes.gouv.fr/',
            license: {
              name: 'Licence Ouverte 2.0',
              url: 'https://www.etalab.gouv.fr/licence-ouverte-open-licence/',
            },
          }],
          `https://data.geopf.fr/wmts?${params}`
              + '&TILEMATRIX=${id.zoom}&TILEROW=${id.y}&TILECOL=${id.x}',
          /* tint= */ 0xFFFFFFFF as RgbaU32,
          /* z= */ Z_BASE_SATELLITE,
          /* extraZoom= */ 0,
          /* minZoom= */ 0,
          // PM_0_19 in the capabilities
          /* maxZoom= */ 19,
          this.renderer,
      ),
      controls: [],
      values: {},
    };
  }

  private newCollectionEntry(
      id: string,
      name: string,
      collection: string,
      copyright: Copyright,
      streams: Stream[],
      locations: LocationIndex): LayerEntry {
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer: new CollectionLayer(
          [copyright],
          `/api/collections/${collection}`,
          /* style= */ undefined,
          // Snapping to a level whose cells are a few pixels across is invisible and saves most of
          // the geometry, but past zoom 10 we want the real boundaries.
          [
            {minZoom: 0, snap: 11},
            {minZoom: 7, snap: 14},
            {minZoom: 10, snap: undefined},
          ],
          streams,
          locations,
          this.camera,
          this.renderer,
      ),
      controls: [],
      values: {},
    };
  }

  // A new layer for each cycle, because each cycle's archive says how its tiles are stored
  private newFaaEntry(product: FaaProduct, name: string, z: number): LayerEntry {
    const layer =
        new SwitchLayer(this.faaCycles[product], cycle =>
            new RasterTileLayer(
                [FAA_COPYRIGHT],
                faaUrl(product, cycle, this.faaArchives[product].format),
                /* tint= */ 0xFFFFFFFF as RgbaU32,
                z,
                /* extraZoom= */ 0,
                /* minZoom= */ 0,
                this.faaArchives[product].maxZoom,
                this.renderer));
    return {
      kind: 'layer',
      id: faaEntryId(product),
      name,
      enabled: false,
      layer,
      controls: [],
      values: {},
      configure: () => {
        layer.show(this.faaCycles[product]);
      },
    };
  }

  // A new layer for each day, because each day's archive carries its own channels and says how its
  // tiles are stored
  private newSnowEntry(id: string, name: string, snow: 'depth'|'water'): LayerEntry {
    const layer =
        new SwitchLayer(this.snodasDay, day =>
            new RasterTileLayer(
                [SNODAS_COPYRIGHT],
                snodasUrl(day, this.snodasArchive.format),
                /* tint= */ 0xFFFFFFFF as RgbaU32,
                /* z= */ Z_OVERLAY_TERRAIN,
                /* extraZoom= */ 0,
                /* minZoom= */ 0,
                this.snodasArchive.maxZoom,
                this.renderer,
                {snow, channels: this.snodasFormat.channels}));
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer,
      controls: [],
      values: {},
      readout: {
        name: `${name} (SNODAS)`,
        read: point => {
          const packed = layer.current.packedAt(point);
          return packed !== undefined
              ? readSnodas(snow, this.snodasFormat.channels, packed)
              : undefined;
        },
      },
      configure: () => {
        layer.show(this.snodasDay);
      },
    };
  }

  // Points each FAA layer at the cycle in effect, or the one before it while ours is late. A
  // cycle's metadata says both that it is tiled and how its tiles are stored.
  private showLatestFaaCharts(): void {
    for (const product of FAA_PRODUCTS) {
      findFirstLoaded(
          [faaCycle(0), faaCycle(-1)],
          cycle => `${FAA_TILES_URL}${product}_${cycle}/metadata`,
          (cycle, response) => {
            response.json().then((metadata: ArchiveMetadata) => {
              this.faaCycles[product] = cycle;
              this.faaArchives[product] = getTileArchive(metadata);
              this.configureEntries(id => id === faaEntryId(product));
            }, () => {});
          });
    }
  }

  // Points the snow layers at the newest tiled day. Its metadata says that the day is there, how its
  // tiles are stored, and how it packs its pixels. Days tiled before the importer wrote channels
  // draw nothing.
  private showLatestSnow(): void {
    findFirstLoaded(
        Array.from({length: SNODAS_LOOKBACK_DAYS + 1}, (_, i) => utcDay(-i)),
        day => `${SNODAS_URL}${day}/metadata`,
        (day, response) => {
          response.json().then((metadata: ArchiveMetadata & Partial<SnodasFormat>) => {
            this.snodasDay = day;
            this.snodasFormat = {channels: metadata.channels ?? []};
            this.snodasArchive = getTileArchive(metadata);
            this.configureEntries(id => id === SNOW_DEPTH_ID || id === SNOW_WATER_ID);
          }, () => {});
        });
  }

  get forecastStart(): number {
    return this.ecmwfRun;
  }

  get forecastEnd(): number {
    return this.ecmwfRun + this.getRunFormat().maxStep * HOUR_MS;
  }

  /**
   * Points the weather layers at the step nearest a time in ms, and the sun and moon at the time.
   * Undefined follows the clock, so the viewer calls this again as the clock moves.
   */
  setTime(time: number|undefined): void {
    this.time = time;
    this.sky.setTime(time ?? Date.now());
    this.configureEntries(id => id.startsWith('weather/'));
  }

  setInteractive(interactive: boolean): void {
    this.sky.setInteractive(interactive);
  }

  /** Moves the sun and moon to a time in ms without touching the weather. */
  previewTime(time: number): void {
    this.sky.setTime(time);
  }

  // Until the newest run's metadata arrives there is no run to show, so an archive draws nothing.
  private getRunFormat(): EcmwfRunFormat {
    return this.ecmwfRunFormat ?? {maxStep: 0, tileSize: 256, layers: {}};
  }

  private getFormat(archive: string): EcmwfFormat {
    return this.getRunFormat().layers[archive] ?? {stepHours: ECMWF_CYCLE_HOURS, channels: []};
  }

  // The step nearest the map's time and the next few, so that playing swaps to loaded tiles
  private getWeatherSteps(archive: string): {url: string; upcoming: string[]} {
    const format = this.getFormat(archive);
    const {stepHours} = format;
    const {maxStep} = this.getRunFormat();
    const step = getEcmwfStep(this.ecmwfRun, this.time ?? Date.now(), format, maxStep);
    const upcoming = [];
    const last = Math.min(step + PREFETCH_STEPS * stepHours, getLastStep(format, maxStep));
    for (let next = step + stepHours; next <= last; next += stepHours) {
      upcoming.push(ecmwfStepUrl(this.ecmwfRun, archive, next, this.ecmwfArchive.format));
    }
    return {url: ecmwfStepUrl(this.ecmwfRun, archive, step, this.ecmwfArchive.format), upcoming};
  }

  private getNextStepTime(at: number, archive: string): number|undefined {
    const format = this.getFormat(archive);
    const fromRun = (at - this.ecmwfRun) / HOUR_MS;
    const next = Math.max(Math.floor(fromRun / format.stepHours) + 1, 0) * format.stepHours;
    const last = getLastStep(format, this.getRunFormat().maxStep);
    return next <= last ? this.ecmwfRun + next * HOUR_MS : undefined;
  }

  private configureEntries(matches: (id: string) => boolean): void {
    for (const {entry} of layerEntries(this.current())) {
      if (matches(entry.id)) {
        entry.configure?.(entry.values);
      }
    }
  }

  private newWeatherEntry(view: WeatherView, name: string): LayerEntry {
    return this.newSwitchedWeatherEntry(`weather/${view}`, name, [], {}, () => ({view, name}));
  }

  // An entry whose controls pick which view it shows. Pick also names the view's readings.
  private newSwitchedWeatherEntry(
      id: string,
      name: string,
      controls: LayerControl[],
      values: ControlValues,
      pick: (values: ControlValues) => {view: WeatherView; name: string}): LayerEntry {
    let picked = pick(values);
    const layer = new SwitchLayer(picked.view, (view: WeatherView) => {
      const {url, upcoming} = this.getWeatherSteps(WEATHER_VIEWS[view].layer);
      return new StepLayer(
          url,
          upcoming,
          stepUrl =>
              new RasterTileLayer(
                  [ECMWF_COPYRIGHT],
                  stepUrl,
                  /* tint= */ 0xFFFFFFFF as RgbaU32,
                  /* z= */ Z_OVERLAY_TERRAIN,
                  /* extraZoom= */ 0,
                  /* minZoom= */ 0,
                  this.ecmwfArchive.maxZoom,
                  this.renderer,
                  {view, channels: this.getFormat(WEATHER_VIEWS[view].layer).channels}));
    });
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer,
      controls,
      values,
      timed: 'forecast',
      readout: {
        get name() {
          return picked.name;
        },
        read: point => {
          const packed = layer.current.current.packedAt(point);
          const {channels} = this.getFormat(WEATHER_VIEWS[picked.view].layer);
          return packed !== undefined ? readWeather(picked.view, channels, packed) : undefined;
        },
      },
      nextTime: at => this.getNextStepTime(at, WEATHER_VIEWS[picked.view].layer),
      configure: values => {
        picked = pick(values);
        layer.show(picked.view);
        const {url, upcoming} = this.getWeatherSteps(WEATHER_VIEWS[picked.view].layer);
        layer.current.show(url, upcoming);
      },
    };
  }

  // Wind or gust speed colors at an altitude, or the strongest to come there, with streamlines or
  // barbs over them
  private newWindEntry(): LayerEntry {
    const pick = (values: ControlValues): {view: WeatherView; name: string} => {
      const level = FLIGHT_LEVELS[Number(values.altitude)];
      const [prefix, now, strongest] =
          values.type === 'gusts'
              ? ['gust', 'Wind gusts', 'Strongest gust']
              : values.altitude === 'ground'
                  ? ['wind', 'Wind', 'Strongest wind']
                  : [`wind_${values.altitude}`, `Wind at ${level}`, `Strongest wind at ${level}`];
      return values.peak === 'none'
          ? {view: prefix as WeatherView, name: now}
          : lookaheadView(`${prefix}_peak`, strongest, values.peak);
    };
    // Every wind view colors its archive's u and v, so the arrows draw the same archive.
    const archive = (values: ControlValues) => WEATHER_VIEWS[pick(values).view].layer;
    const values = {type: 'sustained', altitude: 'ground', peak: 'none', arrows: 'streamlines'};
    const type: LayerControl = {
      kind: 'select',
      key: 'type',
      label: 'Type',
      options: [{value: 'sustained', label: 'Sustained'}, {value: 'gusts', label: 'Gusts'}],
    };
    const altitude =
        altitudeControl(
            [
              {value: 'ground', label: 'Ground'},
              ...WIND_LEVELS.map(level => ({value: String(level), label: FLIGHT_LEVELS[level]})),
            ],
            // ECMWF only forecasts gusts at 10 m.
            values => values.type !== 'gusts');
    const peak = lookaheadControl('peak', 'Strongest');
    const speed =
        this.newSwitchedWeatherEntry('weather/wind', 'Wind', [type, altitude, peak], values, pick);
    let style: WindStyle|undefined = 'streamlines';
    let vectorArchive = archive(values);
    const {url, upcoming} = this.getWeatherSteps(vectorArchive);
    const vectors =
        new StepLayer(
            url,
            upcoming,
            stepUrl => {
              const layer =
                  new WindVectorLayer(
                      [ECMWF_COPYRIGHT],
                      stepUrl,
                      /* z= */ Z_OVERLAY_TERRAIN + 0.5,
                      this.ecmwfArchive.maxZoom,
                      this.getRunFormat().tileSize,
                      this.renderer,
                      this.getFormat(vectorArchive).channels);
              layer.setStyle(style);
              return layer;
            });
    return {
      ...speed,
      layer: new CompositeLayer([speed.layer, vectors]),
      controls: [type, altitude, {
        kind: 'select',
        key: 'arrows',
        label: 'Arrows',
        options: [
          {value: 'none', label: 'None'},
          {value: 'streamlines', label: 'Streamlines'},
          {value: 'barbs', label: 'Barbs'},
        ],
      }, peak],
      configure: values => {
        speed.configure?.(values);
        const arrows = values.arrows;
        style = arrows === 'streamlines' || arrows === 'barbs' ? arrows : undefined;
        for (const layer of vectors.all) {
          layer.setStyle(style);
        }
        vectorArchive = archive(values);
        const {url, upcoming} = this.getWeatherSteps(vectorArchive);
        vectors.show(url, upcoming);
      },
    };
  }

  // Points the weather layers at the newest tiled run. ecmwf.sh writes wind at step 0 last, so we
  // take its metadata, which holds the formats of the whole run, to mean the run is there. Every
  // archive in a run stores its tiles like wind does, see Weather.kt#writeLayer.
  private showLatestWeather(): void {
    const cycleMs = ECMWF_CYCLE_HOURS * HOUR_MS;
    const newest = Math.floor(Date.now() / cycleMs) * cycleMs;
    findFirstLoaded(
        Array.from({length: ECMWF_LOOKBACK_CYCLES + 1}, (_, i) => newest - i * cycleMs),
        run => `${ecmwfArchiveUrl(run, 'wind', 0)}/metadata`,
        (run, response) => {
          response.json().then((metadata: ArchiveMetadata & {ecmwf?: EcmwfRunFormat}) => {
            this.ecmwfRun = run;
            this.ecmwfRunFormat = metadata.ecmwf;
            this.ecmwfArchive = getTileArchive(metadata);
            this.configureEntries(id => id.startsWith('weather/'));
            this.runChanged();
          }, () => {});
        });
  }

  private newArcGisExportEntry(
      id: string,
      name: string,
      copyright: Copyright,
      service: string,
      maxZoom: number): LayerEntry {
    const params = new URLSearchParams({
      bboxSR: '3857',
      imageSR: '3857',
      size: '256,256',
      format: 'png32',
      transparent: 'true',
      f: 'image',
    });
    return {
      kind: 'layer',
      id,
      name,
      enabled: false,
      layer: new RasterTileLayer(
          [copyright],
          `https://mapservices.weather.noaa.gov/${service}/export?bbox=\${bbox}&${params}`,
          /* tint= */ 0xFFFFFFFF as RgbaU32,
          /* z= */ Z_OVERLAY_TERRAIN,
          /* extraZoom= */ 0,
          /* minZoom= */ 0,
          maxZoom,
          this.renderer,
      ),
      controls: [],
      values: {},
    };
  }
}

// Streams for a collection whose importer puts every point in a SimpleS2.HIGHEST_INDEX_LEVEL
// cell, like AviationVectors.kt and Conditions.kt. Below zoom they tile at level 3, where the whole
// world is 384 cells, so a world view costs at most a few hundred requests. From zoom on they tile
// at indexBottom so that a zoomed in view doesn't pull points from far offscreen.
function getPointStreams(zoom: number, indexBottom: number): Stream[] {
  return [
    {minZoom: 0, maxZoom: zoom, indexBottom: 3, ...POINT_LEVELS},
    {minZoom: zoom, maxZoom: undefined, indexBottom, ...POINT_LEVELS},
  ];
}

function getPolygonStreams(): Stream[] {
  return [
    // Anything wider than a level 10 cell, so about 7 km up. That reaches the national parks, which
    // sit at levels 6 to 9, and it is everything the coarsest snap can draw: snapping to level 11
    // erases whatever is smaller than a level 11 cell anyway.
    //
    // Tiled at level 5 rather than 4 because a tile carries its objects across its whole cell, so a
    // coarser tiling means pulling parks hundreds of km offscreen once zoomed in. Level 5 costs 205
    // requests for a view of the western US instead of 68, and 2.9 MB instead of 4.5 MB at zoom 11.
    {minZoom: 0, maxZoom: undefined, indexBottom: 5, fromLevel: 0, toLevel: 10},
    // Everything smaller. A level 6 cell holds up to 11k of these, which is why they wait until the
    // viewport is small enough to be worth it.
    {minZoom: 7, maxZoom: undefined, indexBottom: 6, fromLevel: 11, toLevel: undefined},
  ];
}

// Calls found with the first candidate whose probe URL loads, trying them in order
function findFirstLoaded<T>(
    candidates: T[],
    probe: (candidate: T) => string,
    found: (candidate: T, response: Response) => void): void {
  const [candidate, ...rest] = candidates;
  if (candidate === undefined) {
    return;
  }
  fetch(probe(candidate)).then(response => {
    if (response.ok) {
      found(candidate, response);
    } else {
      findFirstLoaded(rest, probe, found);
    }
  }, () => {});
}

function getTileArchive({format, maxzoom}: ArchiveMetadata): TileArchive {
  return {format, maxZoom: Number(maxzoom)};
}

// The first day of the FAA cycle in effect, offset by whole cycles
function faaCycle(offset: number): string {
  const cycleMs = FAA_CYCLE_DAYS * 24 * HOUR_MS;
  const index = Math.floor((Date.now() - FAA_CYCLE_EPOCH_MS) / cycleMs) + offset;
  return new Date(FAA_CYCLE_EPOCH_MS + index * cycleMs).toISOString().substring(0, 10);
}

function faaEntryId(product: FaaProduct): string {
  return `us/faa_${product}`;
}

function faaUrl(product: FaaProduct, cycle: string, format: string): string {
  return `${FAA_TILES_URL}${product}_${cycle}/\${id.zoom}/\${id.x}/\${id.y}.${format}`;
}

// Today plus offset days, as a UTC date because SNODAS days are UTC
function utcDay(offset: number): string {
  return new Date(Date.now() + offset * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);
}

function altitudeControl(
    options: Array<{value: string; label: string}>,
    appliesTo?: (values: ControlValues) => boolean): LayerControl {
  return {kind: 'select', key: 'altitude', label: 'Altitude', options, appliesTo};
}

function lookaheadControl(key: string, label: string): LayerControl {
  return {
    kind: 'select',
    key,
    label,
    options: [
      {value: 'none', label: 'At map time'},
      ...LOOKAHEAD_WINDOWS.map(([hours, label]) =>
          ({value: String(hours), label: `Next ${label}`})),
    ],
  };
}

function lookaheadView(prefix: string, what: string, window: ControlValues[string]):
    {view: WeatherView; name: string} {
  const [, label] = checkExists(LOOKAHEAD_WINDOWS.find(([hours]) => String(hours) === window));
  return {view: `${prefix}_${window}` as WeatherView, name: `${what}, next ${label}`};
}

// The step nearest to at, or the last one the archive has
function getEcmwfStep(run: number, at: number, format: EcmwfFormat, maxStep: number): number {
  const {stepHours} = format;
  const fromRun = (at - run) / HOUR_MS;
  const nearest = Math.max(Math.round(fromRun / stepHours) * stepHours, 0);
  return Math.min(nearest, getLastStep(format, maxStep));
}

// A lookahead needs the hours after its step, so it stops that much short of the run's end.
function getLastStep({lookaheadHours, stepHours}: EcmwfFormat, maxStep: number): number {
  const last = maxStep - (lookaheadHours ?? 0);
  return last - last % stepHours;
}

function ecmwfArchiveUrl(run: number, archive: string, step: number): string {
  // Runs are named like 2026101518
  const iso = new Date(run).toISOString();
  const name = iso.substring(0, 10).replaceAll('-', '') + iso.substring(11, 13);
  return `${ECMWF_URL}${name}_${archive}_${step}`;
}

function ecmwfStepUrl(run: number, archive: string, step: number, format: string): string {
  return `${ecmwfArchiveUrl(run, archive, step)}/\${id.zoom}/\${id.x}/\${id.y}.${format}`;
}

function snodasUrl(day: string, format: string): string {
  return `${SNODAS_URL}${day}/\${id.zoom}/\${id.x}/\${id.y}.${format}`;
}

// The local day, because that is what a date input shows and edits
function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// An empty day leaves that end open, which STAC writes as ..
function sentinelSearch(values: ControlValues): Search {
  const from = String(values.from);
  const to = String(values.to);
  return {
    // Without a zone, Date reads these as local time.
    from: from ? new Date(`${from}T00:00:00`).toISOString() : '..',
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '..',
    // Sentinel-1 skips cloud filtering because its radar sees through clouds.
    query: values.cloud === undefined ? {} : {'eo:cloud_cover': {lte: values.cloud}},
  };
}

// Defaults to masked true color because older saved views have no bands or fill_clouds value.
function sentinel2Bands(values: ControlValues): Bands {
  const bands = SENTINEL_2_BANDS[String(values.bands)] ?? SENTINEL_2_BANDS.true_color;
  return values.fill_clouds === false ? bands : {...bands, mask: SENTINEL_2_MASK};
}

function byteSample(asset: string, sample: number): Channel {
  return {asset, sample, nearest: false, offset: 0, decibels: false, scale: 1, bias: 0};
}

// Stretches reflectance from 0 to 0.4 across a byte, the gain of 2.5 Sentinel Hub's composites use.
// Sentinel-2 Collection 1 stores reflectance as 0.0001 * (DN - 1000) in every year.
function reflectance(asset: string): Channel {
  return {
    asset,
    sample: 0,
    nearest: false,
    offset: -1000,
    decibels: false,
    scale: 0.0001 * 255 / 0.4,
    bias: 0,
  };
}

// An index like NDVI from two Sentinel-2 bands, written as its indexByte
function normalizedDifference(a: string, b: string): Channel {
  return {
    asset: a,
    sample: 0,
    nearest: false,
    // the 0.0001 scale cancels out of the ratio, but the offset doesn't
    offset: -1000,
    combine: {asset: b, sample: 0, op: 'normalized_difference'},
    decibels: false,
    scale: 127.5,
    bias: 127.5,
  };
}

// Maps -1 to 1 across a byte, the way normalizedDifference writes it
function indexByte(index: number): number {
  return Math.round(127.5 * (index + 1));
}

function decibels(asset: string, over: string|undefined, low: number, high: number): Channel {
  return {
    asset,
    sample: 0,
    nearest: false,
    offset: 0,
    combine: over === undefined ? undefined : {asset: over, sample: 0, op: 'ratio'},
    decibels: true,
    scale: 255 / (high - low),
    bias: -255 * low / (high - low),
  };
}

// https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice
function copernicusCredit(years: string): Copyright[] {
  return [{
    long: `Contains modified Copernicus Sentinel data ${years}`,
    short: `Copernicus Sentinel ${years}`,
  }];
}

// Credits Copernicus and Catalyst separately because Catalyst licenses its terrain correction
// apart from the Copernicus data.
function sentinel1Credit(years: string): Copyright[] {
  return [
    ...copernicusCredit(years),
    {
      long: 'Sentinel-1 RTC by Catalyst',
      short: 'Catalyst',
      url: 'https://planetarycomputer.microsoft.com/dataset/sentinel-1-rtc',
      provider: {
        name: 'Microsoft Planetary Computer',
        url: 'https://planetarycomputer.microsoft.com',
      },
      license: CC_BY_4,
    },
  ];
}
